import classNames from "classnames";
import { useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { useMutation } from "@apollo/client";
import useCapabilities, { useCanManageTasksBool } from "@/hooks/useCapabilities";
import IconButton from "@/components/IconButton";
import type { Action } from "@/components/MenuButton";
import { RowHeader, RowLink, TableCardRow, TABLE_CARD_BODY_PADDING } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import useAmplitude from "@/hooks/useAmplitude";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import type { CRMTask } from "@/types";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import type { Node } from "@/pages/sections/[sectionId]/change-requests/queries";
import DeleteTaskDialog from "@/pages/tasks/_components/DeleteTaskDialog";
import { UPDATE_TASK_MUTATION, type UpdateTaskResponse } from "@/pages/tasks/_hooks/mutations";
import { evictTasksCache } from "@/pages/tasks/queries";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import { taskPreviewSubtitle } from "../_utils/clipTaskDescription";
import { formatDaysUntilDue } from "../_utils/formatDaysUntilDue";
import { MentionedText, displayMentionText } from "@/components/AtMention";
import DashboardPrivateTitle from "./DashboardPrivateTitle";
import DashboardRowDetails from "./DashboardRowDetails";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import DashboardTasksPanelHeader from "./DashboardTasksPanelHeader";
import PendingChangeRequestsTable from "./PendingChangeRequestsTable";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import {
  DASHBOARD_PANEL_BODY_CLASS,
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL
} from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";
import { shouldShowTaskPreviewSection } from "../_utils/todoPanelVisibility";

const COLUMN_COUNT = 2;
const TASK_ICON = "assignment_turned_in" as const;
const DASHBOARD_TASK_SOURCE = "dashboard" as const;

function DueLabel({ dueDate }: { dueDate: string | null }) {
  const dueLabel = formatDaysUntilDue(dueDate);
  if (!dueLabel) {
    return renderEmptyCellState(null);
  }

  return (
    <span
      className={classNames({
        "text-sys-brand-error": dueLabel.tone === "error",
        "text-sys-brand-on-surface-variant": dueLabel.tone === "default"
      })}
    >
      {dueLabel.label}
    </span>
  );
}

function TaskDetails({ name, description, isPrivate }: { name: string; description?: string; isPrivate: boolean }) {
  return (
    <DashboardRowDetails
      icon={TASK_ICON}
      title={
        <DashboardPrivateTitle
          title={<MentionedText content={name} />}
          isPrivate={isPrivate}
          privateTooltip="Task is Private"
        />
      }
      text={description}
    />
  );
}

function TaskRowMenuButton({ taskId, onOpen }: { taskId: string; onOpen: () => void }) {
  return (
    <IconButton
      id={actionId(taskId)}
      name="more_vert"
      skipMargin
      title="actions"
      ariaLabel="Task actions"
      data-testid="actions"
      iconClassName="font-extrabold"
      className="!m-0 !p-0 h-10"
      containerClassName="shrink-0"
      onClick={(event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
    />
  );
}

function RecentTaskRow({ task, rowTestId, onOpenMenu }: { task: CRMTask; rowTestId: string; onOpenMenu: () => void }) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const description = taskPreviewSubtitle(task.name, task.description);
  const openTask = () => {
    trackEvent(TASK_ANALYTICS_EVENTS.opened, {
      ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE),
      openSource: "row"
    });
    navigate(`/tasks/${task.id}` as Path);
  };
  const isPrivate = task.visibility === TaskVisibilityValues.PRIVATE;
  const menuButton = <TaskRowMenuButton taskId={task.id} onOpen={onOpenMenu} />;

  function handleContextMenu(event: MouseEvent<HTMLTableRowElement>) {
    event.preventDefault();
    event.stopPropagation();
    onOpenMenu();
  }

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={rowTestId}
      onClick={openTask}
      asButton={false}
      ariaLabel={`Open task ${displayMentionText(task.name)}`}
      onContextMenu={handleContextMenu}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <TaskDetails name={task.name} description={description} isPrivate={isPrivate} />
            <DueLabel dueDate={task.dueDate} />
          </div>
          {menuButton}
        </div>
      }
    >
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "max-w-0")}>
        <TaskDetails name={task.name} description={description} isPrivate={isPrivate} />
      </td>
      <td className="whitespace-nowrap align-middle py-3 text-body-medium text-sys-brand-on-surface">
        <div className="flex items-center justify-end gap-1">
          <DueLabel dueDate={task.dueDate} />
          {menuButton}
        </div>
      </td>
    </TableCardRow>
  );
}

function TasksPreviewTable({ children, isStackedTableRows }: { children: ReactNode; isStackedTableRows: boolean }) {
  return (
    <table
      className={getDashboardPreviewTableClassName(isStackedTableRows)}
      data-testid="home-recent-tasks-table"
      aria-label="Open tasks"
    >
      {isStackedTableRows ? null : (
        <colgroup>
          <col />
          <col className="w-40" />
        </colgroup>
      )}
      <tbody>{children}</tbody>
    </table>
  );
}

function TaskPreviewSection({
  label,
  count,
  headerTestId,
  countTestId,
  viewMoreTestId,
  viewAllTo,
  tasks,
  loading,
  error,
  errorRowTestId,
  rowTestIdPrefix,
  onOpenMenu
}: {
  label: string;
  count: number;
  headerTestId: string;
  countTestId: string;
  viewMoreTestId: string;
  viewAllTo: Path;
  tasks: CRMTask[];
  loading: boolean;
  error?: Error;
  errorRowTestId: string;
  rowTestIdPrefix: string;
  onOpenMenu: (task: CRMTask) => void;
}) {
  return (
    <>
      <RowHeader label={label} count={count} columnCount={COLUMN_COUNT} testId={countTestId} rowTestId={headerTestId} />
      {loading ? (
        <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
      ) : error ? (
        <tr data-testid={errorRowTestId}>
          <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-body-medium text-sys-brand-error">
            Unable to load tasks. Please try again later.
          </td>
        </tr>
      ) : (
        <>
          {tasks.map((task) => (
            <RecentTaskRow
              key={task.id}
              task={task}
              rowTestId={`${rowTestIdPrefix}${task.id}`}
              onOpenMenu={() => onOpenMenu(task)}
            />
          ))}
          <RowLink columnCount={COLUMN_COUNT} to={viewAllTo} testId={viewMoreTestId}>
            View All
          </RowLink>
        </>
      )}
    </>
  );
}

type RecentTasksTableProps = {
  tasks: CRMTask[];
  loading?: boolean;
  error?: Error;
  totalOpenTaskCount?: number;
  teamTasks?: CRMTask[];
  teamTasksLoading?: boolean;
  teamTasksError?: Error;
  totalOpenTeamTaskCount?: number;
  canViewTasks?: boolean;
  changeRequests?: Node[];
  changeRequestsLoading?: boolean;
  totalPendingChangeRequestCount?: number;
  changeRequestsSectionId?: string | null;
  showChangeRequestsSection?: boolean;
};

export default function RecentTasksTable({
  tasks,
  loading = false,
  error,
  totalOpenTaskCount = 0,
  teamTasks = [],
  teamTasksLoading = false,
  teamTasksError,
  totalOpenTeamTaskCount = 0,
  canViewTasks = true,
  changeRequests = [],
  changeRequestsLoading = false,
  totalPendingChangeRequestCount = 0,
  changeRequestsSectionId,
  showChangeRequestsSection = false
}: RecentTasksTableProps) {
  const canManageTasks = useCanManageTasksBool();
  const { id: currentUserId } = useCapabilities();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { Menu, setMenu } = useTableRowMenu();
  const [taskToDelete, setTaskToDelete] = useState<CRMTask | null>(null);
  const [updateTask, { loading: isUpdatingTask }] = useMutation<UpdateTaskResponse>(UPDATE_TASK_MUTATION, {
    update: evictTasksCache
  });
  const showMyTasks =
    canViewTasks &&
    shouldShowTaskPreviewSection({
      loading,
      error: Boolean(error),
      taskCount: tasks.length,
      totalCount: totalOpenTaskCount
    });
  const showTeamTasks =
    canViewTasks &&
    shouldShowTaskPreviewSection({
      loading: teamTasksLoading,
      error: Boolean(teamTasksError),
      taskCount: teamTasks.length,
      totalCount: totalOpenTeamTaskCount
    });

  const handleCompleteTask = useCallback(
    async (task: CRMTask) => {
      if (isUpdatingTask) return;

      try {
        const result = await updateTask({
          variables: { input: { id: task.id, stage: TaskStageValues.COMPLETE } }
        });

        if (result.data?.updateTask?.success) {
          trackEvent(TASK_ANALYTICS_EVENTS.stageChanged, {
            ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE, { includeStage: false }),
            fromStage: task.stage,
            toStage: TaskStageValues.COMPLETE
          });
          showSnackbar("Task completed");
          return;
        }

        const apiMessage = result.data?.updateTask?.message;
        if (apiMessage) {
          showErrorSnackbar("Task not completed", { subtitle: apiMessage });
        } else {
          showErrorSnackbar("Task not completed", { subtitle: "Please try again later." });
        }
      } catch {
        showErrorSnackbar("Task not completed", { subtitle: "Please try again later." });
      }
    },
    [isUpdatingTask, trackEvent, updateTask]
  );

  const handleAssignToMe = useCallback(
    async (task: CRMTask) => {
      if (isUpdatingTask || !currentUserId) return;

      try {
        const result = await updateTask({
          variables: { input: { id: task.id, assignedToId: currentUserId } }
        });

        if (result.data?.updateTask?.success) {
          trackEvent(TASK_ANALYTICS_EVENTS.updated, {
            ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE),
            fieldsChanged: ["assignedToId"]
          });
          showSnackbar("Task assigned to you");
          return;
        }

        const apiMessage = result.data?.updateTask?.message;
        if (apiMessage) {
          showErrorSnackbar("Task not assigned", { subtitle: apiMessage });
        } else {
          showErrorSnackbar("Task not assigned", { subtitle: "Please try again later." });
        }
      } catch {
        showErrorSnackbar("Task not assigned", { subtitle: "Please try again later." });
      }
    },
    [currentUserId, isUpdatingTask, trackEvent, updateTask]
  );

  const getTaskActions = useCallback(
    (task: CRMTask, options?: { allowAssignToMe?: boolean }): Action[] => {
      const actions: Action[] = [];
      const canAssignToMe = Boolean(options?.allowAssignToMe && currentUserId && task.assignedTo?.id !== currentUserId);

      if (canAssignToMe) {
        actions.push({
          section: "Manage",
          text: "Assign to Me",
          testId: "assign-to-me-action",
          onClick: () => void handleAssignToMe(task)
        });
      }

      if (task.stage === TaskStageValues.OPEN) {
        actions.push({
          section: "Manage",
          text: "Complete Task",
          onClick: () => void handleCompleteTask(task)
        });
      }

      actions.push({
        section: "Actions",
        text: "View Task",
        onClick: () => {
          trackEvent(TASK_ANALYTICS_EVENTS.opened, {
            ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE),
            openSource: "row_action"
          });
          navigate(`/tasks/${task.id}` as Path);
        }
      });

      if (canManageTasks) {
        actions.push({
          text: "Delete Task",
          iconName: "delete",
          destructive: true,
          onClick: () => setTaskToDelete(task)
        });
      }

      return actions;
    },
    [canManageTasks, currentUserId, handleAssignToMe, handleCompleteTask, navigate, trackEvent]
  );

  const openTaskMenu = useCallback(
    (task: CRMTask, options?: { allowAssignToMe?: boolean }) => {
      setMenu({ rowId: task.id, actions: getTaskActions(task, options) });
    },
    [getTaskActions, setMenu]
  );

  return (
    <section data-testid="home-recent-tasks-card" className={DASHBOARD_PANEL_SHELL_CLASS}>
      <Menu />
      <DashboardTasksPanelHeader
        canViewTasks={canViewTasks}
        showMyTasks={showMyTasks}
        showTeamTasks={showTeamTasks}
        showChangeRequestsSection={showChangeRequestsSection}
      />
      <div
        className={classNames(
          "max-w-full",
          TABLE_CARD_BODY_PADDING,
          DASHBOARD_PANEL_BODY_CLASS,
          DASHBOARD_TABLE_CARD_BODY_CLASS,
          "flex flex-col gap-0"
        )}
      >
        {showMyTasks || showTeamTasks ? (
          <TasksPreviewTable isStackedTableRows={isStackedTableRows}>
            {showMyTasks ? (
              <TaskPreviewSection
                label="My Tasks"
                count={totalOpenTaskCount}
                headerTestId="home-my-tasks-header"
                countTestId="home-my-tasks-count"
                viewMoreTestId="recent-tasks-view-more"
                viewAllTo="/tasks/my-tasks"
                tasks={tasks}
                loading={loading}
                error={error}
                errorRowTestId="home-recent-tasks-error-row"
                rowTestIdPrefix="recent-task-row-"
                onOpenMenu={openTaskMenu}
              />
            ) : null}
            {showTeamTasks ? (
              <TaskPreviewSection
                label="Team Tasks"
                count={totalOpenTeamTaskCount}
                headerTestId="home-team-tasks-header"
                countTestId="home-team-tasks-count"
                viewMoreTestId="recent-team-tasks-view-more"
                viewAllTo={"/tasks/department-tasks?unassigned=true" as Path}
                tasks={teamTasks}
                loading={teamTasksLoading}
                error={teamTasksError}
                errorRowTestId="home-team-tasks-error-row"
                rowTestIdPrefix="recent-team-task-row-"
                onOpenMenu={(task) => openTaskMenu(task, { allowAssignToMe: true })}
              />
            ) : null}
          </TasksPreviewTable>
        ) : null}
        {showChangeRequestsSection && changeRequestsSectionId ? (
          <PendingChangeRequestsTable
            changeRequests={changeRequests}
            loading={changeRequestsLoading}
            totalPendingCount={totalPendingChangeRequestCount}
            sectionId={changeRequestsSectionId}
          />
        ) : null}
      </div>
      <DeleteTaskDialog
        open={!!taskToDelete}
        taskId={taskToDelete?.id ?? ""}
        task={taskToDelete ?? undefined}
        source={DASHBOARD_TASK_SOURCE}
        onClose={() => setTaskToDelete(null)}
        onDeleted={() => setTaskToDelete(null)}
      />
    </section>
  );
}
